import React, { useEffect, useState, useCallback } from 'react';
import { Check, X, CalendarBlank, Clock } from '@phosphor-icons/react';

const STATUS_META = {
  present: { letter: 'P', cls: 'bg-green-500/15 text-green-600' },
  absent: { letter: 'A', cls: 'bg-red-500/15 text-red-500' },
  leave: { letter: 'L', cls: 'bg-blue-500/15 text-blue-500' },
  half_day: { letter: 'H', cls: 'bg-amber-500/15 text-amber-500' },
  holiday: { letter: 'HO', cls: 'bg-purple-500/15 text-purple-400' },
};

const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }); }
  catch { return ''; }
};

export const AttendanceTab = ({ api, isAdmin }) => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [register, setRegister] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [cell, setCell] = useState(null); // {staff, day, rec}
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [regRes, lvRes] = await Promise.all([
        api.get(`/staff/attendance/register?month=${month}`),
        api.get('/staff/leaves/all?status=pending'),
      ]);
      setRegister(regRes.data);
      setLeaves(lvRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api, month]);

  useEffect(() => { load(); }, [load]);

  const markStatus = async (staffId, day, status) => {
    try {
      await api.post(`/staff/${staffId}/attendance/mark`, { date: `${month}-${day}`, status });
      setCell(null);
      load();
    } catch (err) { alert(err?.response?.data?.detail || 'Failed'); }
  };

  const reviewLeave = async (leaveId, action) => {
    try {
      await api.post(`/staff/leaves/${leaveId}/review`, { action });
      load();
    } catch (err) { alert(err?.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  const days = register?.days_in_month || 30;

  return (
    <div className="space-y-4" data-testid="attendance-tab">
      {/* Pending leave requests */}
      {isAdmin && leaves.length > 0 && (
        <div className="glass-card p-4" data-testid="pending-leaves-panel">
          <p className="overline mb-3">Leave Requests Pending</p>
          <div className="space-y-2">
            {leaves.map(l => (
              <div key={l.leave_id} className="flex items-center justify-between gap-3 rounded-xl border border-blue-500/20 bg-blue-500/5 px-4 py-2.5" data-testid={`leave-req-${l.leave_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{l.staff_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {l.from_date}{l.to_date !== l.from_date ? ` → ${l.to_date}` : ''}{l.reason ? ` — ${l.reason}` : ''}
                  </p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => reviewLeave(l.leave_id, 'approve')} data-testid={`leave-approve-${l.leave_id}`}
                    className="p-2 rounded-lg bg-green-500/10 text-green-600 hover:bg-green-500/20"><Check size={16} weight="bold" /></button>
                  <button onClick={() => reviewLeave(l.leave_id, 'reject')} data-testid={`leave-reject-${l.leave_id}`}
                    className="p-2 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20"><X size={16} weight="bold" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Month picker + legend */}
      <div className="flex flex-wrap items-center gap-3">
        <input type="month" value={month} onChange={e => setMonth(e.target.value)} data-testid="register-month-input"
          className="px-3 py-2 rounded-xl bg-muted/50 border border-border text-sm outline-none" />
        <div className="flex flex-wrap gap-2 text-[11px] ml-auto">
          {Object.entries(STATUS_META).map(([k, v]) => (
            <span key={k} className={`px-2 py-0.5 rounded-full font-bold ${v.cls}`}>{v.letter} = {k.replace('_', ' ')}</span>
          ))}
        </div>
      </div>

      {/* Register grid */}
      <div className="glass-card overflow-x-auto" data-testid="attendance-register">
        <table className="text-xs border-collapse min-w-full">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="text-left px-3 py-2 font-medium text-muted-foreground sticky left-0 bg-card z-10 min-w-[140px]">Staff</th>
              {Array.from({ length: days }, (_, i) => (
                <th key={i} className="px-1 py-2 font-medium text-muted-foreground text-center w-8">{i + 1}</th>
              ))}
              <th className="px-3 py-2 font-medium text-muted-foreground text-center whitespace-nowrap">Summary</th>
            </tr>
          </thead>
          <tbody>
            {(register?.staff || []).map(s => (
              <tr key={s.staff_id} className="border-b border-border last:border-0" data-testid={`register-row-${s.staff_id}`}>
                <td className="px-3 py-2 sticky left-0 bg-card z-10">
                  <p className="font-semibold whitespace-nowrap">{s.name}</p>
                  <p className="text-[10px] text-muted-foreground whitespace-nowrap">
                    {s.role}{s.expected_arrival ? ` · due ${s.expected_arrival}` : ''} · leaves left: {s.leave_balance}/{s.leave_quota}
                  </p>
                </td>
                {Array.from({ length: days }, (_, i) => {
                  const dd = String(i + 1).padStart(2, '0');
                  const rec = s.days[dd];
                  const meta = rec ? STATUS_META[rec.status] : null;
                  return (
                    <td key={dd} className="px-0.5 py-1 text-center">
                      <button
                        onClick={() => isAdmin && setCell({ staff: s, day: dd, rec })}
                        disabled={!isAdmin}
                        data-testid={`cell-${s.staff_id}-${dd}`}
                        title={rec ? `${rec.status}${rec.check_in ? ` · in ${fmtTime(rec.check_in)}` : ''}${rec.late_minutes ? ` · ${rec.late_minutes}m late` : ''}` : 'No record'}
                        className={`w-7 h-7 rounded-md text-[10px] font-bold relative transition-transform ${isAdmin ? 'hover:scale-110' : ''} ${meta ? meta.cls : 'bg-muted/40 text-muted-foreground/40'}`}>
                        {meta ? meta.letter : '·'}
                        {rec?.late_minutes > 0 && <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-orange-500" />}
                      </button>
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-center whitespace-nowrap text-[10px]" data-testid={`summary-${s.staff_id}`}>
                  <span className="text-green-600 font-bold">{s.summary.present}P</span>{' '}
                  <span className="text-red-500 font-bold">{s.summary.absent}A</span>{' '}
                  <span className="text-blue-500 font-bold">{s.summary.leave}L</span>{' '}
                  <span className="text-amber-500 font-bold">{s.summary.half_day}H</span>
                  <span className="block text-muted-foreground mt-0.5">
                    {s.summary.worked_hours}h · {s.summary.late_count} late
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {(register?.staff || []).length === 0 && <p className="text-center text-muted-foreground py-10 text-sm">No active staff</p>}
      </div>
      <p className="text-[11px] text-muted-foreground flex items-center gap-1">
        <Clock size={12} /> Orange dot = late arrival. No-shows are auto-marked absent at 10 PM; forgotten checkouts auto-close (8h cap).
      </p>

      {/* Cell edit modal */}
      {cell && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="cell-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setCell(null)} />
          <div className="relative bg-card border border-border rounded-2xl p-5 w-full max-w-xs">
            <p className="font-semibold text-sm mb-1">{cell.staff.name} — {month}-{cell.day}</p>
            {cell.rec?.check_in && (
              <p className="text-xs text-muted-foreground mb-1">
                In {fmtTime(cell.rec.check_in)}{cell.rec.check_out ? ` · Out ${fmtTime(cell.rec.check_out)} · ${cell.rec.worked_hours}h` : ' · not checked out'}
                {cell.rec.late_minutes > 0 && <span className="text-orange-500"> · {cell.rec.late_minutes}m late</span>}
              </p>
            )}
            {cell.rec?.notes && <p className="text-[11px] text-muted-foreground mb-2 italic">{cell.rec.notes}</p>}
            <div className="grid grid-cols-2 gap-2 mt-3">
              {Object.entries(STATUS_META).map(([k, v]) => (
                <button key={k} onClick={() => markStatus(cell.staff.staff_id, cell.day, k)} data-testid={`mark-${k}`}
                  className={`py-2.5 rounded-xl text-xs font-bold capitalize ${v.cls} ${cell.rec?.status === k ? 'ring-2 ring-[#FF6B35]' : ''} hover:scale-[1.03] transition-transform`}>
                  {k.replace('_', ' ')}
                </button>
              ))}
              <button onClick={() => setCell(null)} className="py-2.5 rounded-xl text-xs font-medium border border-border hover:bg-muted col-span-2">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const SalarySuggestModal = ({ api, staff, cs = 'Rs.', onClose }) => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/staff/${staff.staff_id}/salary-suggest?month=${month}`).then(r => setData(r.data)).catch(() => {});
  }, [api, staff.staff_id, month]);

  const saveRecord = async () => {
    setSaving(true);
    try {
      await api.post(`/staff/${staff.staff_id}/salary`, {
        month, base_amount: data.base_salary, bonus: 0,
        deductions: data.suggested_deductions, advance: data.suggested_advance_recovery,
        notes: `Auto: ${data.absent_days}A + ${data.unpaid_leave_days} unpaid leave + ${data.half_days} half-day${data.suggested_advance_recovery > 0 ? ` · petty-cash recovery ${cs}${data.suggested_advance_recovery}` : ''}`,
      });
      alert('Salary record saved');
      onClose();
    } catch (err) { alert(err?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="salary-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-2xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold">Salary — {staff.name}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted"><X size={18} /></button>
        </div>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)} data-testid="salary-month-input"
          className="px-3 py-2 rounded-xl bg-muted/50 border border-border text-sm outline-none mb-4" />
        {!data ? <p className="text-sm text-muted-foreground py-4 text-center">Loading…</p> : (
          <div className="space-y-2 text-sm" data-testid="salary-breakdown">
            <Row label="Base salary" val={`${cs}${data.base_salary.toLocaleString('en-IN')}`} />
            <Row label={`Attendance (${data.present_days}P · ${data.absent_days}A · ${data.leave_days}L · ${data.half_days}H)`} val={`${cs}${data.per_day_rate}/day`} muted />
            <Row label={`Unpaid leave (quota ${data.paid_leave_quota})`} val={`${data.unpaid_leave_days} day${data.unpaid_leave_days !== 1 ? 's' : ''}`} muted />
            <Row label={`Deductions (${data.deduction_days} days)`} val={`- ${cs}${data.suggested_deductions.toLocaleString('en-IN')}`} red={data.suggested_deductions > 0} />
            <Row label={`Petty cash balance ${cs}${data.petty_cash_balance.toLocaleString('en-IN')}`} val={data.suggested_advance_recovery > 0 ? `- ${cs}${data.suggested_advance_recovery.toLocaleString('en-IN')}` : 'no recovery'} red={data.suggested_advance_recovery > 0} muted={data.suggested_advance_recovery === 0} />
            <div className="border-t border-border pt-2 flex justify-between font-bold text-base">
              <span>Suggested Net Pay</span>
              <span className="text-[#22C55E]" data-testid="salary-net">{cs}{data.suggested_net.toLocaleString('en-IN')}</span>
            </div>
            <button onClick={saveRecord} disabled={saving} data-testid="save-salary-record-btn"
              className="w-full py-3 mt-2 rounded-full bg-[#FF6B35] text-white btn-glow font-semibold text-sm disabled:opacity-50">
              {saving ? 'Saving…' : 'Save Salary Record'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

function Row({ label, val, red, muted }) {
  return (
    <div className={`flex justify-between gap-3 ${muted ? 'text-muted-foreground text-xs' : ''}`}>
      <span>{label}</span>
      <span className={`font-medium whitespace-nowrap ${red ? 'text-red-500' : ''}`}>{val}</span>
    </div>
  );
}
